
import * as React from 'react';
import { css } from '@uifabric/utilities/lib/css';
import * as ProgressItemTileStyles from './ProgressItemTile.scss';
import { ProgressIndicator } from 'office-ui-fabric-react/lib/ProgressIndicator';
import { WithItemCache, IItemCacheStateProps } from '../../withItemCache/WithItemCache';
import { progressControl } from '../../../controls/progressControl';
import { ItemFileTypeIcon } from '../../itemFileTypeIcon/ItemFileTypeIcon';
import {
  getItemCountsByProgressPhase,
  getOverallProgressPhase,
  getOverallSimpleProgressBreakdown
} from '../../../operations/progress';
import { IconButton } from 'office-ui-fabric-react/lib/components/Button/IconButton/IconButton';
import { format, getLocalizedCountValue } from '@ms/utilities-strings/lib/index';
import ProgressStrings from '../ProgressStrings.resx';
import { ProgressPhase, sourceItemFacet } from '../../../facets/ProgressFacets';
import { displayNameFacet } from '../../../facets/ItemFacets';
import { errorFacet } from '../../../facets/DemandFacets';
import { WithAction, IActionStateProps } from '../../withAction/WithAction';
import { CancelProgressAction } from '../actions/cancel/CancelProgressAction';
import { ExpireProgressAction } from '../actions/expire/ExpireProgressAction';
import { isCancel } from '../../../utilities/async/CancelError';
import { ICommand } from '../../../models/commands/ICommand';
import { ActionCommandButtonStrip } from '../../actionCommandButtonStrip/ActionCommandButtonStrip';
import { ControlHandlerProvider } from '../../decorators/withControlHandler';
import { IItemCache } from '../../../models/IItemCacheObserver';
import { selectedItemsControl } from '../../../controls/selectedItemsControl';

const NO_COMMANDS: ICommand[] = [];

const titleFormatters = {
  [ProgressPhase.started]: (count: number) => format(getLocalizedCountValue(
    ProgressStrings.genericStartedItemsTitle,
    ProgressStrings.genericCompletedItemsTitleIntervals,
    count) || '',
    count),
  [ProgressPhase.completed]: (count: number) => format(getLocalizedCountValue(
    ProgressStrings.genericCompletedItemsTitle,
    ProgressStrings.genericCompletedItemsTitleIntervals,
    count) || '',
    count),
  [ProgressPhase.failed]: (count: number) => format(getLocalizedCountValue(
    ProgressStrings.genericFailedItemsTitle,
    ProgressStrings.genericFailedItemsTitleIntervals,
    count) || '',
    count),
  [ProgressPhase.canceled]: (count: number) => format(getLocalizedCountValue(
    ProgressStrings.genericFailedItemsTitle,
    ProgressStrings.genericFailedItemsTitleIntervals,
    count) || '',
    count),
};

const descriptions: { [K in ProgressPhase]?: string; } = {
  [ProgressPhase.created]: ProgressStrings.genericCreatedDescription,
  [ProgressPhase.canceled]: ProgressStrings.genericCanceledDescription,
  [ProgressPhase.completed]: ProgressStrings.genericCompletedDescription
};

/**
 * @public
 */
export interface IProgressTileProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  percentComplete?: number;
  actions?: React.ReactNode;
  cancel?: React.ReactNode;
  progressHidden?: boolean;
  error?: Error;
}

/**
 * Component which renders the layout for a single Progress item.
 * Should be moved to Fabric as a complete version of ProgressIndicator.
 * @public
 */
export class ProgressTile extends React.PureComponent<IProgressTileProps> {
  public render(): JSX.Element {
    const {
      icon,
      actions,
      cancel,
      description,
      title
    } = this.props;

    const progressTitle = title ? (
      <div className={ css(ProgressItemTileStyles.title) }>{ title }</div>
    ) : title;

    const progressDescription = description ? (
      <div className={ css(ProgressItemTileStyles.description) }>{ description }</div>
    ) : description;

    return (
      <div
        className={ css(ProgressItemTileStyles.tile, {
          [ProgressItemTileStyles.hasIcon]: !!icon,
          [ProgressItemTileStyles.error]: !!this.props.error
        }) }
      >
        {
          icon ? (
            <span className={ css(ProgressItemTileStyles.icon) }>
              { this.props.icon }
            </span>
          ) : null
        }
        <div className={ css(ProgressItemTileStyles.progress) }>
          <ProgressIndicator
            label={ progressTitle }
            description={ progressDescription }
            percentComplete={ this.props.percentComplete }
            progressHidden={ this.props.progressHidden }
          />
        </div>
        {
          actions ?
            (
              <div className={ css(ProgressItemTileStyles.actions) }>
                { actions }
              </div>

            ) : null
        }
        {
          cancel ? (
            <span className={ css(ProgressItemTileStyles.cancel) }>
              { cancel }
            </span>
          ) : null
        }
      </div>
    );
  }
}

/**
 * @public
 */
export interface IProgressItemTileProps {
  itemKey: string;
  format: 'group' | 'item';
}

/**
 * @public
 */
export class ProgressItemTile extends React.PureComponent<IProgressItemTileProps> {
  public render(): JSX.Element {
    const {
      itemKey,
      format: progressFormat
    } = this.props;

    return (
      <WithItemCache>
        { ({ itemCache, dispatch }: IItemCacheStateProps): JSX.Element => {
          const sourceItem = itemCache.demandItemFacet(sourceItemFacet, itemKey);
          const sourceItemKey = sourceItem && sourceItem.itemKey;

          const displayName =
            itemCache.demandItemFacet(displayNameFacet, itemKey) ||
            itemCache.demandItemFacet(displayNameFacet, sourceItemKey);

          const error = itemCache.demandItemFacet(errorFacet, itemKey);

          const controlHandler = itemCache.demandItemFacet(progressControl, itemKey);

          const itemCountsByPhase = getItemCountsByProgressPhase(itemCache, {
            itemKey: itemKey
          });

          const {
            phase,
            count
          } = getOverallSimpleProgressBreakdown(itemCache, { itemKey });

          const exactPhase = getOverallProgressPhase(itemCache, { itemKey });

          const defaultPercentComplete = phase !== ProgressPhase.started ?
            1 :
            undefined;

          const {
            title,
            description,
            percentComplete,
            commands
          } = progressControl.evaluate(controlHandler)(itemCache, {
            itemKey: this.props.itemKey,
            title: displayName || titleFormatters[phase](count),
            description: error && !isCancel(error) ? error.message : descriptions[exactPhase],
            ...(defaultPercentComplete ? { percentComplete: defaultPercentComplete } : {}),
            itemCountsByPhase: itemCountsByPhase,
            format: progressFormat,
            commands: NO_COMMANDS
          });

          const cancelAction = phase === 'started' ? (
            <CancelProgressAction itemKeys={ [itemKey] } />
          ) : phase === 'failed' ? (
            <ExpireProgressAction itemKeys={ [itemKey] } />
          ) : null;

          const selectedItemsControlHandler: typeof selectedItemsControl.type = (
            itemCache2: IItemCache,
            props2: typeof selectedItemsControl.inputType
          ): typeof selectedItemsControl.returnType => {
            return {
              itemKeys: [itemKey]
            };
          };

          return (
            <ControlHandlerProvider
              controlHandlers={
                selectedItemsControl.pack(selectedItemsControlHandler)
              }
            >
              <WithAction
                action={ cancelAction }
              >
                { ({ execute, isAvailable }: IActionStateProps) => {
                  const onClickCancel = isAvailable ? () => {
                    execute().catch(() => {
                      // Ignore errors.
                    });
                  } : undefined;

                  return (
                    <ProgressTile
                      title={ progressFormat === 'item' && displayName || title }
                      description={ description }
                      percentComplete={ percentComplete }
                      error={ error && !isCancel(error) ? error : undefined }
                      progressHidden={ phase === 'completed' || phase === 'canceled' ? true : undefined }
                      icon={
                        progressFormat === 'item' ? (
                          <ItemFileTypeIcon
                            itemKey={ sourceItemKey }
                            size={ 48 }
                          />
                        ) : undefined
                      }
                      cancel={
                        onClickCancel ? (
                          <IconButton
                            iconProps={
                              {
                                iconName: 'Cancel'
                              }
                            }
                            ariaLabel={ phase === 'started' ?
                              ProgressStrings.progresslistCancelButtonAriaLabel :
                              ProgressStrings.progresslistRemoveButtonAriaLabel
                            }
                            onClick={ onClickCancel }
                          />
                        ) : undefined
                      }
                      actions={
                        commands.length > 0 ?
                          (
                            <ActionCommandButtonStrip commands={ commands } />
                          ) :
                          undefined
                      }
                    />
                  );
                } }
              </WithAction>
            </ControlHandlerProvider>
          );
        } }
      </WithItemCache>
    );
  }
}
